/**
 * Focus-scoped keyboard input.
 *
 * In a multi-client (split-screen) game every client's systems run in the SAME page and register their
 * keydown/keyup listeners on the SAME global `window`. Without help, one physical keypress fires EVERY client's
 * handler, so each client would have to hand-gate on "am I focused?" — a footgun the generated code routinely
 * got wrong (cross-client input bleed, flaky multi-client tests).
 *
 * This module makes `addEventListener('keydown'|'keyup'|'keypress', listener)` on `window` and `document`
 * focus-aware. A listener added while a client is the "registering client" is OWNED by that client and fires
 * only while that client is focused — as if every client had a window of its own. Otherwise an owned listener
 * behaves like a native one: a `once` listener is consumed by its own client's key only; adding the same
 * listener twice registers it once; its capture and bubble registrations are separate; a `handleEvent` object is
 * owned like a function; `this` is the target; an aborted `signal` removes it; and a listener it adds while
 * handling a key belongs to the same client. A listener added while no client is registering stays native and
 * hears every key.
 *
 * Who registers as which client: `scopeProcessInputToClient` runs a process's entry points — and the area hooks
 * the client calls on its systems itself — as its client (the playground does this for every client, at creation,
 * through `registerClientCreationHook`); a host may instead set `setRegisteringClient` around a client's creation.
 * That window is STICKY — every client's code runs inside it — so a key-listener removal made there is attributed to
 * no client: the listener leaves the page, as on the one window of an unscoped page. The input driver sets the focused client(s) before dispatching a
 * key. There is intentionally NO public `isFocused` API for system code.
 *
 * Focus is a SET — MULTIPLE clients can be focused at once (so "drive both players simultaneously" is just
 * "focus both"). An EMPTY set means "deliver to everyone" (single-client / focus not yet established).
 *
 * Tokens are opaque (the client index or join id); both ends of a given wiring must use the same token type.
 * All of this state lives on `globalThis`, shared by every copy of this module in the page.
 */
import type { MockClient } from './types.js';
import { focusScopedInputState } from './focus-scoped-input-state.js';

/** Identifies the client a key listener belongs to. */
export type ClientToken = string | number;

/**
 * Mark which client is currently registering listeners (set around its lifecycle) — a sticky window, until
 * `clearRegisteringClient`. A key listener added inside it belongs to `token`. A removal made inside it is attributed
 * to no client, since other clients' code runs inside the window too: the listener leaves the page — its native
 * registration and every client's copy — as on the one window of an unscoped page.
 */
export function setRegisteringClient(token: ClientToken | null): void {
  focusScopedInputState().registeringClient = token;
}
/** No client is registering listeners: a key listener added now belongs to no client. */
export function clearRegisteringClient(): void {
  focusScopedInputState().registeringClient = null;
}
/**
 * Run `fn` with `token` as the registering client, restoring the prior value after. A key-listener removal `fn` makes
 * is `token`'s own: it removes `token`'s copy only — also inside another client's sticky `setRegisteringClient`
 * window.
 */
export function runAsRegisteringClient<T>(token: ClientToken | null, fn: () => T): T {
  const state = focusScopedInputState();
  const prev = state.registeringClient;
  const prevCalling = state.callingClient;
  state.registeringClient = token;
  state.callingClient = token;
  try {
    return fn();
  } finally {
    state.registeringClient = prev;
    state.callingClient = prevCalling;
  }
}

/**
 * Replace the focus set with EXACTLY this one client (`null` clears focus →
 * deliver to everyone). Back-compat single-focus helper the input driver uses
 * before a keypress so that press routes to one client.
 */
export function setFocusedClient(token: ClientToken | null): void {
  const { focusedClients } = focusScopedInputState();
  focusedClients.clear();
  if (token !== null) focusedClients.add(token);
}
/** Replace the focus set with this set of clients (multiple may be focused). */
export function setFocusedClients(tokens: Iterable<ClientToken>): void {
  const { focusedClients } = focusScopedInputState();
  focusedClients.clear();
  for (const t of tokens) focusedClients.add(t);
}
/** Add one client to the focus set (focus an additional client without unfocusing others). */
export function addFocusedClient(token: ClientToken): void {
  focusScopedInputState().focusedClients.add(token);
}
/** Remove one client from the focus set. */
export function removeFocusedClient(token: ClientToken): void {
  focusScopedInputState().focusedClients.delete(token);
}
/** Empty the focus set: a key reaches every client. */
export function clearFocusedClients(): void {
  focusScopedInputState().focusedClients.clear();
}
/** Internal: is this client currently focused? (drives the UI highlight, never exposed to systems.) */
export function isClientFocused(token: ClientToken): boolean {
  const { focusedClients } = focusScopedInputState();
  return focusedClients.size === 0 || focusedClients.has(token);
}
/** The focus set, in the order its clients were added; empty when a key reaches every client. */
export function getFocusedClients(): ClientToken[] {
  return [...focusScopedInputState().focusedClients];
}

/** Test-only: reset the registering client and the focus set between suites. Scoped processes and creation hooks stay. */
export function resetFocusScopedInput(): void {
  const state = focusScopedInputState();
  state.registeringClient = null;
  state.focusedClients.clear();
}

const KEY_EVENTS = new Set(['keydown', 'keyup', 'keypress']);
const INSTALLED = '__focusScopedInputInstalled';

/** One client's registration of one listener for one (type, capture): the wrapper the target holds natively. */
interface OwnedRegistration {
  readonly wrapper: (this: EventTarget, event: Event) => void;
  /** Detaches what forgets this registration when its `signal` aborts. */
  readonly release: () => void;
}

const isListener = (listener: unknown): listener is EventListenerOrEventListenerObject =>
  typeof listener === 'function' || (typeof listener === 'object' && listener !== null);

const captureOf = (options: unknown): boolean =>
  typeof options === 'boolean' ? options : Boolean((options as EventListenerOptions | null | undefined)?.capture);

/** Calls `listener` the way the platform does: a function with `this` = the current target, an object through its `handleEvent`. */
const invoke = (listener: EventListenerOrEventListenerObject, event: Event): void => {
  if (typeof listener === 'function') listener.call(event.currentTarget, event);
  else listener.handleEvent(event);
};

function patchTarget(target: EventTarget | null | undefined): void {
  if (!target) return;
  const t = target as EventTarget & Record<string, unknown>;
  if (t[INSTALLED]) return;
  t[INSTALLED] = true;

  const origAdd = target.addEventListener.bind(target);
  const origRemove = target.removeEventListener.bind(target);
  // listener → `${type}|${capture}` → owner → registration. The platform keeps one registration per (type,
  // listener, capture); here each client stands for a target of its own. A WeakMap: dropped with the listener.
  const owned = new WeakMap<object, Map<string, Map<ClientToken, OwnedRegistration>>>();
  const slotOf = (type: string, capture: boolean) => `${type}|${capture}`;

  const removeOwned = (type: string, listener: object, capture: boolean, owner: ClientToken): void => {
    const slots = owned.get(listener);
    const byOwner = slots?.get(slotOf(type, capture));
    const registration = byOwner?.get(owner);
    if (!slots || !byOwner || !registration) return;
    byOwner.delete(owner);
    if (byOwner.size === 0) slots.delete(slotOf(type, capture));
    registration.release();
    origRemove(type, registration.wrapper, capture);
  };

  t.addEventListener = function (type: string, listener: unknown, options?: boolean | AddEventListenerOptions): void {
    const owner = focusScopedInputState().registeringClient;
    if (!KEY_EVENTS.has(type) || owner === null || !isListener(listener)) {
      origAdd(type, listener as EventListenerOrEventListenerObject | null, options);
      return;
    }
    const capture = captureOf(options);
    const opts = typeof options === 'object' && options !== null ? options : undefined;
    // The platform adds nothing for an already-aborted signal.
    if (opts?.signal?.aborted) return;
    let slots = owned.get(listener);
    if (!slots) owned.set(listener, (slots = new Map()));
    let byOwner = slots.get(slotOf(type, capture));
    if (!byOwner) slots.set(slotOf(type, capture), (byOwner = new Map()));
    // The platform's dedup — per client.
    if (byOwner.has(owner)) return;
    const once = Boolean(opts?.once);
    const wrapper = function (this: EventTarget, event: Event): void {
      const { focusedClients } = focusScopedInputState();
      if (focusedClients.size !== 0 && !focusedClients.has(owner)) return;
      // The platform removes a `once` listener just before calling it.
      if (once) removeOwned(type, listener, capture, owner);
      // Run as its client: a listener this one adds while handling the key belongs to the same client.
      runAsRegisteringClient(owner, () => invoke(listener, event));
    };
    const signal = opts?.signal;
    const onAbort = () => removeOwned(type, listener, capture, owner);
    signal?.addEventListener('abort', onAbort);
    byOwner.set(owner, { wrapper, release: () => signal?.removeEventListener('abort', onAbort) });
    // `once` is the router's to honour: registered natively, a key another client presses would consume it.
    origAdd(type, wrapper, opts ? { ...opts, once: false } : options);
  };

  t.removeEventListener = function (type: string, listener: unknown, options?: boolean | EventListenerOptions): void {
    if (!KEY_EVENTS.has(type) || !isListener(listener)) {
      origRemove(type, listener as EventListenerOrEventListenerObject | null, options);
      return;
    }
    const capture = captureOf(options);
    const state = focusScopedInputState();
    const owner = state.registeringClient;
    const byOwner = owned.get(listener)?.get(slotOf(type, capture));
    // Inside a client's own call (`runAsRegisteringClient`: its entry point, its key listener), from a client holding
    // a copy: that copy, as its own target would remove it.
    if (owner !== null && owner === state.callingClient && byOwner?.has(owner)) {
      removeOwned(type, listener, capture, owner);
      return;
    }
    // Otherwise the removal is attributed to no client — none is registering (a timer, test code); the one that is
    // holds no copy (a page-wide unbind); or it is a sandbox runtime's sticky `setRegisteringClient` window, inside
    // which every client's code runs, so a removal there cannot be told apart from another client's — and the
    // listener leaves the page: its native registration and every client's copy, as `removeEventListener` on the one
    // window of an unscoped page would.
    origRemove(type, listener, options);
    if (byOwner) {
      for (const client of [...byOwner.keys()]) removeOwned(type, listener, capture, client);
    }
  };
}

/**
 * Patch keyboard `addEventListener`/`removeEventListener` on BOTH `window` and
 * `document` so key listeners are routed only to the focused client, regardless
 * of which target the generated code listens on. Idempotent per target. No-op
 * where the DOM is absent.
 */
export function installFocusScopedInput(win?: (Window & typeof globalThis) | null): void {
  const w = win ?? (typeof window !== 'undefined' ? window : undefined);
  patchTarget(w);
  patchTarget(typeof document !== 'undefined' ? document : (w as any)?.document);
}

/** Whether `installFocusScopedInput` has patched `target` — the page's `window` unless one is named. */
export function isFocusScopedInputInstalled(target: EventTarget | null | undefined = typeof window !== 'undefined' ? window : undefined): boolean {
  return Boolean(target && (target as EventTarget & Record<string, unknown>)[INSTALLED]);
}

/** A process `scopeProcessInputToClient` can scope: the entry points it runs a client's systems through. Any it lacks are skipped. */
export interface ScopableProcess {
  startLoop: (...args: never[]) => unknown;
  update: (...args: never[]) => unknown;
  addSystem?: (...args: never[]) => unknown;
  addSystemsArrayFromFile?: (...args: never[]) => unknown;
  deliverToSystem?: (...args: never[]) => unknown;
  stopLoop?: (...args: never[]) => unknown;
  removeSystem?: (...args: never[]) => unknown;
  stopGame?: (...args: never[]) => unknown;
  clear?: (...args: never[]) => unknown;
}

/**
 * The entry points a client's systems run in, besides message delivery: `onInit` (`addSystem`); `onStart`
 * (`startLoop`, and `addSystemsArrayFromFile` — which starts an area's systems itself when the client joins an
 * area after its game started); `onUpdate` (`update`); and the teardown that runs `onStop`/`onClear`
 * (`stopLoop`; `removeSystem`, which leaving an area goes through; `stopGame`; `clear`), so a listener a system
 * removes there leaves its own client's copy only.
 */
const CLIENT_ENTRY_POINTS = ['addSystem', 'addSystemsArrayFromFile', 'startLoop', 'update', 'stopLoop', 'removeSystem', 'stopGame', 'clear'] as const;

/**
 * The hooks the CLIENT calls on a system itself — its area join, leave and state handling (`Client`) — outside every
 * process entry point. `onJoinedArea` is where a system first can talk to its area, so it is a natural place to start
 * listening for keys.
 */
const CLIENT_CALLED_SYSTEM_HOOKS = ['onJoinedArea', 'onLeftArea', 'onUpdatedAreaState', 'onAreaSystemStateRemoved'] as const;

/** Makes the client-called hooks of `process`'s systems run as `token`, wrapping each system once. */
const scopeSystemHooks = (process: Record<string, unknown>, token: ClientToken): void => {
  const systems = process.systemArray;
  if (!Array.isArray(systems)) return;
  const { scopedSystems } = focusScopedInputState();
  for (const system of systems) {
    if (!system || typeof system !== 'object' || scopedSystems.has(system)) continue;
    scopedSystems.add(system);
    const s = system as Record<string, unknown>;
    for (const hook of CLIENT_CALLED_SYSTEM_HOOKS) {
      const original = s[hook];
      if (typeof original !== 'function') continue;
      s[hook] = function (this: unknown, ...args: unknown[]) {
        return runAsRegisteringClient(token, () => original.apply(this, args));
      };
    }
  }
};

/**
 * Make every keyboard listener a process's systems register be OWNED by `token`: its entry points run as that
 * client — `onInit` (`addSystem`), `onStart` (`startLoop`, and `addSystemsArrayFromFile` for an area joined after
 * the game started), `onUpdate` (`update`), `onServerMessage` (`deliverToSystem`, including a message buffered
 * while the system's file initialized), `onStop`/`onClear` (`stopLoop`, `removeSystem`, `stopGame`, `clear`), and
 * the hooks the client calls on a system itself when it joins, leaves or updates an area (`onJoinedArea`,
 * `onLeftArea`, `onUpdatedAreaState`, `onAreaSystemStateRemoved`) — so the listeners only fire while that client is
 * focused, and a listener the client removes there leaves its copy only. A removal by a client holding no copy of
 * the listener (or by no client, or inside a sticky `setRegisteringClient` window) takes it off the page, as native
 * `removeEventListener` would. Listeners
 * registered anywhere else (a timer, after an `await` inside those hooks) belong to no client and hear every key.
 * Idempotent per process; scoping a process already scoped to another client throws.
 */
export function scopeProcessInputToClient(process: ScopableProcess, token: ClientToken): void {
  const { scopedProcesses } = focusScopedInputState();
  const scopedTo = scopedProcesses.get(process);
  if (scopedTo !== undefined) {
    if (scopedTo !== token) {
      throw new Error(`[test-utils] scopeProcessInputToClient: this process is already scoped to client ${String(scopedTo)}, so it cannot be scoped to client ${String(token)}.`);
    }
    return;
  }
  scopedProcesses.set(process, token);
  const p = process as unknown as Record<string, unknown>;
  for (const method of CLIENT_ENTRY_POINTS) {
    const original = p[method];
    if (typeof original !== 'function') continue;
    p[method] = function (this: unknown, ...args: unknown[]) {
      const result = runAsRegisteringClient(token, () => original.apply(this, args));
      // A system just added is one whose client-called hooks must run as this client too.
      if (method === 'addSystem' || method === 'addSystemsArrayFromFile') {
        scopeSystemHooks(p, token);
        if (result && typeof (result as { then?: unknown }).then === 'function') {
          (result as Promise<unknown>).then(() => scopeSystemHooks(p, token), () => undefined);
        }
      }
      return result;
    };
  }
  scopeSystemHooks(p, token);
  // `onServerMessage`: the DELIVERY runs as the client, not just the call. A message that arrives while the
  // system's game/area file is still initializing is buffered, and the process's own flush delivers it later,
  // outside this call.
  const deliverToSystem = p.deliverToSystem;
  if (typeof deliverToSystem === 'function') {
    p.deliverToSystem = function (this: unknown, systemName: unknown, deliver: (system: unknown) => void, ...rest: unknown[]) {
      return deliverToSystem.call(this, systemName, (system: unknown) => runAsRegisteringClient(token, () => deliver(system)), ...rest);
    };
  }
}

/** The token `scopeProcessInputToClient` scoped `process` under, or `undefined` when it never scoped it. */
export function getScopedInputToken(process: object): ClientToken | undefined {
  return focusScopedInputState().scopedProcesses.get(process);
}

/** What test-utils calls for every client it creates — see `registerClientCreationHook`. */
export type ClientCreationHook = (client: MockClient) => void;

/**
 * Calls `hook` for every client test-utils creates from now on — `env.createClient()`, both `createClients`
 * forms, `initClients`, in any environment `initializer` boots — synchronously as the client is constructed:
 * before any `onClientCreated`, before it authenticates, and before it joins (whose systems may register key
 * listeners). It runs before the client is recorded anywhere, so a hook that throws fails that creation and leaves
 * no trace of the client: not in `env.clients`, the default-auth numbering or a connector's load. Registering the
 * same function again is a no-op; the returned function unregisters it. The registry is shared by every copy of this
 * module in the page. With nothing registered, creating a client is exactly what it was. The playground registers
 * two: its input scoping, before a playground's `setup` runs, and — in a page `bootstrapPlayground` boots — the
 * recorder of each client's canvas that `--video`'s frame gate reads.
 */
export function registerClientCreationHook(hook: ClientCreationHook): () => void {
  const { clientCreationHooks } = focusScopedInputState();
  clientCreationHooks.add(hook);
  return () => {
    clientCreationHooks.delete(hook);
  };
}
